import clsx from "clsx";
import { useAtom } from "jotai";
import { useCallback, useMemo } from "react";
import { match } from "ts-pattern";

import { getTotalSweepEntries, handleAssetUrl } from "../../../shared/utils";
import { FKC_MAX_ENTRIES } from "../../fkc.constants";
import { linkedCardsAtom, pagesAtom } from "../../fkc.state";
import { Pages } from "../../fkc.types";

interface LayoutProps {
  children: React.ReactNode;
  progress: number;
}

export function Layout({ children, progress }: LayoutProps): JSX.Element {
  const [page] = useAtom(pagesAtom);
  const [linkedCards] = useAtom(linkedCardsAtom);

  const skipFkc = useCallback((): void => {
    window.theme.FkcUtils.skip();
  }, []);

  const totalEntries = useMemo((): number => {
    const submission = window.theme.Sweepstake.prototype.retrieveSubmission();
    return getTotalSweepEntries(submission?.data);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- update entries only when linkedCards changes
  }, [linkedCards]);

  const buttonLabel = useMemo((): string => {
    if (linkedCards) {
      return "Continue to next step";
    }

    return match(page)
      .with(Pages.PLAID, () => "No thanks, proceed to next step")
      .otherwise(() => "No thanks, continue without signing up");
  }, [linkedCards, page]);

  if (page === Pages.OPTIN) {
    return <div className="fkc__container">{children}</div>;
  }

  return (
    <div className="fkc__container">
      <div className={clsx("lander__cpl__quiz__header", "fkc__header")}>
        <div
          className={clsx(
            "lander__cpl__quiz__header--earned-entries",
            "fkc__header--earned-entries"
          )}
        >
          {`Your entries ${totalEntries}`}
        </div>

        <img
          src={handleAssetUrl("fkc_logo.svg")}
          alt="FindKeepCash Logo"
          className="fkc__logo"
        />

        <div className="fkc__content">
          <div className="fkc__content__header--entries--value">
            +{FKC_MAX_ENTRIES} Entries
          </div>

          <div>
            <span
              className={clsx(
                "lander__cpl__quiz__header--description",
                "fkc__description"
              )}
            >
              Join FindKeepCash and earn up to {FKC_MAX_ENTRIES} extra entries
            </span>
            <div className="lander__quiz__header--progress-bar--container">
              <div className="progress-bar-container">
                <div
                  className="progress-bar-indicator"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          <div className="fkc_children--container">
            <div className="fkc__children">{children}</div>
            <div className="lander__quiz__footer">
              <button
                className="lander__quiz__footer--skip-btn"
                onClick={skipFkc}
              >                
                {buttonLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
